import { isDefined } from "@clipboard-health/util-ts";
import { Button, Card, CardContent, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { generatePath, useHistory, useRouteMatch } from "react-router-dom";

import { useGetShiftIdWithOrientationRequired } from "../../redesign/Shift/OrientationBlock/useGetShiftIdWithOrientationRequired";
import { type ShiftBlockWithMappedDetails } from "../../redesign/ShiftDiscovery/Blocks/api/useFetchPaginatedShiftBlocks";
import { SHIFT_BLOCKS_PATH } from "../../redesign/ShiftDiscovery/Blocks/paths";
import { SHIFT_BLOCK_ACTIONS } from "../../redesign/ShiftDiscovery/Blocks/utils";
import { PriorityAccessAlert } from "./PriorityAccessAlert";
import { ShiftBlockFacilityDetails } from "./ShiftBlockFacilityDetails";
import { ShiftBlockHeader } from "./ShiftBlockHeader";

interface ShiftBlockItemProps {
  shiftBlock: ShiftBlockWithMappedDetails;
  agentTimezone: string;
  shouldShowFacilityDetails: boolean;
  showStartEndTime?: boolean;
}

export function ShiftBlockItem(props: ShiftBlockItemProps) {
  const { shiftBlock, agentTimezone, shouldShowFacilityDetails, showStartEndTime } = props;
  const { id: shiftBlockId, facility, isShiftBlockAssigned, priorityAccess } = shiftBlock;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: facility.id,
  });

  const history = useHistory();
  const { path: currentRouterUrl } = useRouteMatch();
  const shiftIdWithOrientationRequired = useGetShiftIdWithOrientationRequired();

  const handleViewBlockDetails = () => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.VIEW_BLOCK_DETAILS_TAPPED,
      shiftBlockId,
      facilityId: facility.id,
      location: currentRouterUrl,
    });
    const path = generatePath(`${RootPaths.APP_V2_HOME}/${SHIFT_BLOCKS_PATH}/:shiftBlockId`, {
      shiftBlockId,
    });

    const params = new URLSearchParams();

    if (isDefined(shiftIdWithOrientationRequired)) {
      params.set("shiftIdWithOrientationRequired", shiftIdWithOrientationRequired);
    }

    history.replace({
      pathname: path,
      search: `?${params.toString()}`,
    });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <ShiftBlockHeader
            shiftBlockWithDetails={shiftBlock}
            agentTimezone={agentTimezone}
            mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
            showStartEndTime={showStartEndTime}
          />
          <Stack spacing={2}>
            {isDefined(priorityAccess) && isDefined(priorityAccess.priorityTill) && (
              <Stack spacing={1}>
                {isDefined(priorityAccess) && isDefined(priorityAccess.priorityTill) && (
                  <PriorityAccessAlert priorityAccessTill={priorityAccess.priorityTill} />
                )}
              </Stack>
            )}

            {shouldShowFacilityDetails && (
              <ShiftBlockFacilityDetails
                facility={facility}
                isShiftBlockAssigned={isShiftBlockAssigned}
              />
            )}

            <Button variant="contained" onClick={handleViewBlockDetails}>
              View Block Details
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
